import React, { Component } from 'react';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import SuccessionPlanningWelcome from './SuccessionPlanningWelcome';
import SuccessionPlanningFormField from './SuccessionPlanningFormField';
import SuccessionPlanningProgressBar from './SuccessionPlanningProgressBar';
import SuccessionPlanningSummaryPage from './SuccessionPlanningSummaryPage';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import SuccessionPageLayout from './SuccessionPageLayout';

// Import the questionnaire schema
import { questionnaireSchema } from './succession-questionnaire-schema';

// GraphQL mutations
const SAVE_SUCCESSION_PLANNING_DATA = gql`
  mutation SaveSuccessionPlanningFormData(
    $formData: String!
    $currentSection: Int!
    $sectionCount: Int!
    $status: String
    $welcomeShown: Boolean
  ) {
    saveSuccessionPlanningFormData(
      formData: $formData
      currentSection: $currentSection
      sectionCount: $sectionCount
      status: $status
      welcomeShown: $welcomeShown
    ) {
      successionPlanningFormDatum {
        id
        formData
        currentSection
        sectionCount
        completionPercentage
        status
        welcomeShown
      }
      errors
    }
  }
`;

const FINALIZE_SUCCESSION_PLANNING = gql`
  mutation FinalizeSuccessionPlanning {
    finalizeSuccessionPlanning {
      successionPlanningFormDatum {
        id
        formData
        currentSection
        sectionCount
        completionPercentage
        status
        welcomeShown
        aiInsights {
          key_strengths
          identified_risks
          action_recommendations
          compliance_status
          emotional_readiness_score
          financial_readiness_score
          overall_readiness_score
        }
      }
      errors
    }
  }
`;

// Loading spinner component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-succession-primary"></div>
  </div>
);

// Navigation button component
const NavigationButton = ({ onClick, disabled, direction, children, isSubmitting = false }) => {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    borderRadius: '0.375rem',
    outline: 'none',
    transition: 'all 0.3s ease',
    cursor: disabled || isSubmitting ? 'not-allowed' : 'pointer',
    opacity: disabled || isSubmitting ? 0.5 : 1,
  };
  
  const primaryStyle = {
    ...baseStyle,
    color: '#ffffff',
    backgroundColor: 'var(--succession-primary)',
    border: '1px solid transparent',
  };
  
  const secondaryStyle = {
    ...baseStyle,
    color: 'var(--succession-text-primary)',
    backgroundColor: 'var(--succession-bg-primary)',
    border: '1px solid var(--succession-border)',
  };
  
  const handleMouseEnter = (e) => {
    if (!disabled && !isSubmitting) {
      if (direction === 'next') {
        e.target.style.backgroundColor = 'var(--succession-primary-hover)';
      } else {
        e.target.style.backgroundColor = 'var(--succession-bg-secondary)';
      }
    }
  };
  
  const handleMouseLeave = (e) => {
    if (!disabled && !isSubmitting) {
      if (direction === 'next') {
        e.target.style.backgroundColor = 'var(--succession-primary)';
      } else {
        e.target.style.backgroundColor = 'var(--succession-bg-primary)';
      }
    }
  };
  
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isSubmitting}
      style={direction === 'next' ? primaryStyle : secondaryStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {direction === 'previous' && <ChevronLeftIcon className="w-4 h-4 mr-2" />}
      {isSubmitting ? <LoadingSpinner /> : children}
      {direction === 'next' && <ChevronRightIcon className="w-4 h-4 ml-2" />}
    </button>
  );
};

class SuccessionPlanningGenerator extends React.Component {
  constructor(props) {
    super(props);
    const { successionPlanningData, company, current_user } = props;
    
    // Component mounting flag to prevent state updates on unmounted components
    this._isMounted = true;
    
    // Debug logging
    console.log("SuccessionPlanningGenerator initialized with data:", successionPlanningData);
    console.log("SuccessionPlanningGenerator aiInsights:", successionPlanningData?.aiInsights);
    
    let initialFormData = {};
    let initialSection = 0;
    
    // If we have data, initialize state from it
    if (successionPlanningData && successionPlanningData.formData) {
      try {
        initialFormData = JSON.parse(successionPlanningData.formData);
        initialSection = successionPlanningData.currentSection || 0;
      } catch (error) {
        console.error('Error parsing Succession Planning form data:', error);
      }
    }
    
    // Determine if we should show the summary page immediately based on completion status
    const isCompleted = successionPlanningData?.status === 'completed';
    
    // Determine if this is a new user with not_started status and welcome not shown
    const isNewUser = !successionPlanningData || successionPlanningData.status === 'not_started';
    const welcomeShown = successionPlanningData?.welcomeShown || false;
    
    this.state = {
      currentSection: initialSection,
      formData: initialFormData,
      loading: false,
      error: null,
      showingSummary: isCompleted, // Show summary page immediately if completed
      aiInsights: successionPlanningData?.aiInsights || null,
      showingWelcomeScreen: isNewUser && !welcomeShown,
      welcomeShown: welcomeShown
    };
    
    // Debug logging for state initialization
    console.log("SuccessionPlanningGenerator initial state aiInsights:", this.state.aiInsights);
  }

  // Handle field change
  handleFieldChange = (fieldId, value) => {
    const { currentSection, formData } = this.state;
    const section = questionnaireSchema[currentSection];
    
    const updatedFormData = {
      ...formData,
      [section.id]: {
        ...formData[section.id],
        [fieldId]: value
      }
    };
    
    this.setState({ formData: updatedFormData });
  };

  // Handle navigation to next section
  handleNext = (saveSuccessionPlanningFormData) => {
    const { currentSection } = this.state;
    const nextSection = Math.min(currentSection + 1, questionnaireSchema.length - 1);
    
    this.setState({ currentSection: nextSection });
    this.saveData(saveSuccessionPlanningFormData);
  };

  // Handle navigation to previous section
  handlePrevious = () => {
    const { currentSection } = this.state;
    const prevSection = Math.max(currentSection - 1, 0);
    
    this.setState({ currentSection: prevSection });
  };

  // Save progress
  handleSaveProgress = (saveSuccessionPlanningFormData) => {
    this.saveData(saveSuccessionPlanningFormData);
  };

  // Finalize assessment
  handleFinalize = (finalizeSuccessionPlanning) => {
    this.setState({ loading: true });
    
    finalizeSuccessionPlanning({
      variables: {}
    }).then(({ data }) => {
      console.log('Finalize response:', data);
      if (this._isMounted) {
        if (data.finalizeSuccessionPlanning.errors.length === 0) {
          this.setState({
            showingSummary: true,
            aiInsights: data.finalizeSuccessionPlanning.successionPlanningFormDatum.aiInsights,
            loading: false
          });
        } else {
          this.setState({ 
            error: data.finalizeSuccessionPlanning.errors.join(', '),
            loading: false 
          });
        }
      }
    }).catch((error) => {
      console.error('Error finalizing succession planning:', error);
      if (this._isMounted) {
        this.setState({ 
          error: error.message,
          loading: false 
        });
      }
    });
  };

  // Save data helper
  saveData = (saveSuccessionPlanningFormData) => {
    const { formData, currentSection } = this.state;
    
    this.setState({ loading: true });
    
    saveSuccessionPlanningFormData({
      variables: {
        formData: JSON.stringify(formData),
        currentSection: currentSection,
        sectionCount: questionnaireSchema.length,
        status: 'in_progress',
        welcomeShown: true
      }
    }).then(() => {
      if (this._isMounted) {
        this.setState({ loading: false });
      }
    }).catch((error) => {
      console.error('Error saving succession planning data:', error);
      if (this._isMounted) {
        this.setState({ 
          error: error.message,
          loading: false 
        });
      }
    });
  };

  // Handle back to editor from summary
  handleBackToEditor = () => {
    this.setState({ showingSummary: false });
  };

  // Handle start planning from welcome screen
  handleStartPlanning = (saveSuccessionPlanningFormData) => {
    this.setState({
      showingWelcomeScreen: false,
      welcomeShown: true
    });
    
    this.saveData(saveSuccessionPlanningFormData);
  };

  componentDidMount() {
    const { successionPlanningData } = this.props;
    
    // Check if we need to update the AI insights from the props
    if (successionPlanningData?.aiInsights && !this.state.aiInsights && this._isMounted) {
      console.log("Updating aiInsights from data in componentDidMount");
      this.setState({ aiInsights: successionPlanningData.aiInsights });
    }
  }

  componentWillUnmount() {
    // Cancel any pending promises or subscriptions
    this._isMounted = false;
  }

  // Helper methods for SuccessionPageLayout
  getPageTitle = () => {
    const { showingWelcomeScreen, showingSummary } = this.state;
    if (showingWelcomeScreen) {
      return "Succession Planning";
    }
    if (showingSummary) {
      return "Succession Plan Summary";
    }
    return "Succession Planning Questionnaire";
  };

  getPageSubtitle = () => {
    const { showingWelcomeScreen, showingSummary } = this.state;
    if (showingWelcomeScreen) {
      return "Create a comprehensive succession plan for your financial advisory practice";
    }
    if (showingSummary) {
      return "Review your completed succession plan and insights";
    }
    return "Complete your succession planning questionnaire to build your plan";
  };

  getActionButtons = (finalizeSuccessionPlanning = null) => {
    const { showingWelcomeScreen, showingSummary } = this.state;
    const { successionPlanningData } = this.props;
    const isCompleted = successionPlanningData?.status === 'completed';

    if (showingWelcomeScreen) {
      return []; // No buttons on welcome screen
    }

    if (showingSummary) {
      return [
        {
          text: "Edit Plan",
          onClick: this.handleBackToEditor,
          icon: "edit",
          variant: "default"
        },
        {
          text: "Download PDF", 
          onClick: () => window.location.href = '/succession/succession_planning/download',
          icon: "export",
          variant: "primary"
        }
      ];
    }

    // Questionnaire view
    const buttons = [];
    
    if (isCompleted && finalizeSuccessionPlanning) {
      buttons.push({
        text: "View Summary",
        onClick: () => this.handleFinalize(finalizeSuccessionPlanning),
        variant: "primary"
      });
    }

    return buttons;
  };

  render() {
    const { 
      currentSection, formData, loading, error, 
      showingSummary, aiInsights, showingWelcomeScreen 
    } = this.state;
    const { successionPlanningData } = this.props;
    const isCompleted = successionPlanningData?.status === 'completed';
    
    return (
      <Mutation mutation={FINALIZE_SUCCESSION_PLANNING}>
        {(finalizeSuccessionPlanning, { loading: finalizeLoading }) => (
          <Mutation mutation={SAVE_SUCCESSION_PLANNING_DATA}>
            {(saveSuccessionPlanningFormData, { loading: mutationLoading }) => {
              // Update the loading state
              const isLoading = loading || mutationLoading || finalizeLoading;
              
              // Show welcome screen for new users
              if (showingWelcomeScreen) {
                return (
                  <SuccessionPageLayout
                    title={this.getPageTitle()}
                    subtitle={this.getPageSubtitle()}
                    actionButtons={this.getActionButtons()}
                  >
                    <div className="max-w-4xl mx-auto">
                      <SuccessionPlanningWelcome onStartClick={() => this.handleStartPlanning(saveSuccessionPlanningFormData)} />
                    </div>
                  </SuccessionPageLayout>
                );
              }
              
              // If showing summary, render the summary page
              if (showingSummary) {
                console.log("SuccessionPlanningGenerator rendering summary page with aiInsights:", aiInsights);
                console.log("SuccessionPlanningGenerator rendering summary page with formData:", formData);
                return (
                  <SuccessionPlanningSummaryPage 
                    formData={formData} 
                    onRevise={this.handleBackToEditor}
                    aiInsights={aiInsights}
                  />
                );
              }
              
              // Get current section
              const section = questionnaireSchema[currentSection];
              if (!section) return (
                <div className="min-h-screen bg-succession-bg-secondary p-6">
                  <div className="max-w-2xl mx-auto bg-succession-bg-primary rounded-lg p-6 border border-succession-border">
                    <div className="text-succession-text-primary text-center">Loading...</div>
                  </div>
                </div>
              );
              
              const isFirstSection = currentSection === 0;
              const isLastSection = currentSection === questionnaireSchema.length - 1;
              const currentSectionData = formData[section.id] || {};
              
              return (
                <SuccessionPageLayout
                  title={this.getPageTitle()}
                  subtitle={this.getPageSubtitle()}
                  actionButtons={this.getActionButtons(finalizeSuccessionPlanning)}
                >
                  <div className="max-w-4xl mx-auto space-y-6">
                    {isCompleted && (
                      <div className="flex items-center py-2 px-4 bg-succession-success bg-opacity-10 border border-succession-success rounded-md">
                        <span className="text-sm text-succession-success">
                          Your succession plan is already complete. You can make edits here or{' '}
                          <button 
                            type="button" 
                            className="text-succession-success underline font-medium" 
                            onClick={() => this.handleFinalize(finalizeSuccessionPlanning)}
                          >
                            return to the summary page
                          </button>.
                        </span>
                      </div>
                    )}
                    
                    {error && (
                      <div className="bg-succession-error bg-opacity-10 border border-succession-error p-4 rounded-md">
                        <div className="flex">
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-succession-error">Error saving data</h3>
                            <div className="mt-2 text-sm text-succession-error">
                              <p>{error}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Progress Bar */}
                    <SuccessionPlanningProgressBar
                      currentSection={currentSection}
                      totalSections={questionnaireSchema.length}
                      showPercentage={true}
                      showSectionCounter={false}
                      size="default"
                    />

                    {/* Main Content */}
                    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6 transition-colors duration-300">
                      <h2 className="text-xl font-semibold mb-2 text-succession-text-primary transition-colors duration-300">
                        {section.title}
                      </h2>
                      {section.description && (
                        <p className="text-succession-text-secondary mb-6 transition-colors duration-300">
                          {section.description}
                        </p>
                      )}
                      
                      {/* Form Fields */}
                      <div className="space-y-6">
                        {section.questions.map((question) => (
                          <SuccessionPlanningFormField
                            key={question.id}
                            type={question.type === 'text' && question.multiline ? 'textarea' : question.type}
                            label={question.question}
                            value={currentSectionData[question.id]}
                            onChange={(value) => this.handleFieldChange(question.id, value)}
                            placeholder={question.placeholder}
                            required={question.required}
                            options={question.options}
                            rows={question.multiline ? 4 : 3}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Navigation */}
                    <div className="bg-succession-bg-primary rounded-b-lg p-6 border border-succession-border border-t-0 transition-colors duration-300">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm text-succession-text-secondary transition-colors duration-300">
                            Section {currentSection + 1} of {questionnaireSchema.length}
                          </span>
                        </div>
                        
                        <div className="flex space-x-3">
                          {!isFirstSection && (
                            <NavigationButton
                              onClick={() => this.handlePrevious()}
                              direction="previous"
                              disabled={isLoading}
                            >
                              Previous
                            </NavigationButton>
                          )}
                          
                          <NavigationButton
                            onClick={
                              isLastSection 
                              ? () => this.handleFinalize(finalizeSuccessionPlanning) 
                              : () => this.handleNext(saveSuccessionPlanningFormData)
                            }
                            direction="next"
                            disabled={isLoading}
                            isSubmitting={isLoading}
                          >
                            {isLastSection ? 'Complete Assessment' : 'Next'}
                          </NavigationButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </SuccessionPageLayout>
              );
            }}
          </Mutation>
        )}
      </Mutation>
    );
  }
}

export default SuccessionPlanningGenerator; 